// src/hooks/useMapState.ts
import * as React from 'react';
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Map as LeafletMap } from 'leaflet';

// Define the context type
interface MapStateContextType {
  selectedVariable: string;
  setSelectedVariable: (variable: string) => void;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  highlightedRegion: string | null;
  setHighlightedRegion: (region: string | null) => void;
  selectedRegion: string | null;
  setSelectedRegion: (region: string | null) => void;
  mapRef: LeafletMap | null;
  setMapRef: (map: LeafletMap | null) => void;
  colorScaleType: string;
  setColorScaleType: (scale: string) => void;
  hoveredVariables: string[];
  setHoveredVariables: (vars: string[]) => void;
  zoomToRegion: (regionId: string) => void;
  resetView: () => void;
}

// Create context with default values
const MapStateContext = createContext<MapStateContextType | undefined>(undefined);

// Provider component
export const MapStateProvider: React.FC<{ 
  children: ReactNode;
  initialYear?: number;
  initialVariable?: string;
}> = ({ 
  children, 
  initialYear = 2023, 
  initialVariable = 'co2' 
}) => {
  const [selectedVariable, setSelectedVariable] = useState<string>(initialVariable);
  const [selectedYear, setSelectedYear] = useState<number>(initialYear);
  const [highlightedRegion, setHighlightedRegion] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [mapRef, setMapRef] = useState<LeafletMap | null>(null);
  const [colorScaleType, setColorScaleType] = useState<string>('viridis');
  const [hoveredVariables, setHoveredVariables] = useState<string[]>([]);

  // Function to zoom to a specific region
  const zoomToRegion = useCallback((regionId: string) => {
    if (!mapRef) return;
    
    try {
      // Find the layer with the matching region ID
      let targetLayer = null;
      mapRef.eachLayer((layer: any) => {
        if (layer.feature && layer.feature.properties && layer.feature.properties.NUTS_ID === regionId) {
          targetLayer = layer;
        }
      });
      
      if (targetLayer) {
        const bounds = targetLayer.getBounds();
        const paddingOptions = {
          padding: [20, 20],
          maxZoom: 8 // Prevent zooming too close
        };
        mapRef.fitBounds(bounds, paddingOptions);
        setSelectedRegion(regionId);
      }
    } catch (error) {
      console.error('Error zooming to region:', error);
      // Fallback: try simple center view
      try {
        mapRef.setView([50, 10], 6);
        setSelectedRegion(regionId);
      } catch (fallbackError) {
        console.error('Error with fallback zoom operation:', fallbackError);
      }
    }
  }, [mapRef, setSelectedRegion]);

  // Function to reset the map view
  const resetView = useCallback(() => {
    if (!mapRef) return;
    
    try {
      mapRef.setView([50, 10], 4);
      setSelectedRegion(null);
    } catch (error) {
      console.error('Error resetting map view:', error);
      // Try alternative reset approach
      try {
        mapRef.setView([50, 10], 5);
        setSelectedRegion(null);
      } catch (fallbackError) {
        console.error('Error with fallback reset operation:', fallbackError);
      }
    }
  }, [mapRef, setSelectedRegion]);

  const value = {
    selectedVariable,
    setSelectedVariable,
    selectedYear,
    setSelectedYear,
    highlightedRegion,
    setHighlightedRegion,
    selectedRegion,
    setSelectedRegion,
    mapRef,
    setMapRef,
    colorScaleType,
    setColorScaleType,
    hoveredVariables,
    setHoveredVariables,
    zoomToRegion,
    resetView
  };

  return (
    <MapStateContext.Provider value={value}>
      {children}
    </MapStateContext.Provider>
  );
};

// Custom hook to use the map state
export const useMapState = () => {
  const context = useContext(MapStateContext);
  if (context === undefined) {
    throw new Error('useMapState must be used within a MapStateProvider');
  }
  return context;
};
